// src/pages/TermsOfUsePage.tsx
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  termsTitle,
  termsIntro,
  termsEnacted,
  termsArticles,
  LJ,
} from '../content/termsContent';

type Lang = 'ja' | 'zh';

const L = (x: LJ, lang: Lang) => x[lang];

const contactCopy = {
  title: { ja: 'お問い合わせ', zh: '联系方式' } as LJ,
  body: {
    ja: '本規約に関するお問い合わせは、お問い合わせページよりご連絡ください。',
    zh: '关于本条款的咨询，请通过“联系我们”页面与我们联系。',
  } as LJ,
  cta: { ja: 'お問い合わせへ', zh: '前往联系我们' } as LJ,
};

const TermsOfUsePage: React.FC = () => {
  const { i18n } = useTranslation();
  const lang: Lang = useMemo(
    () => (i18n.language?.toLowerCase().startsWith('zh') ? 'zh' : 'ja'),
    [i18n.language]
  );

  const list = (arr: string[]) => (
    <ul className="list-disc pl-6 text-slate-700 space-y-2">
      {arr.map((li, i) => (
        <li key={i}>{li}</li>
      ))}
    </ul>
  );

  return (
    <div className="pt-0 sm:pt-14 pb-10 sm:pb-20 bg-gray-50 font-sans">
      <Helmet>
        <title>{L(termsTitle, lang)} - Melodis Clinic</title>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-4xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          <h1 className="text-xl md:text-2xl font-semibold text-slate-800 mb-6 text-center">
            {L(termsTitle, lang)}
          </h1>

          <p className="text-slate-700 leading-relaxed mb-8 text-base md:text-lg">
            {L(termsIntro, lang)}
          </p>

          {termsArticles.map((a, idx) => (
            <section className="mb-10" key={idx}>
              <h2 className="text-base font-semibold text-slate-800 mb-3">{L(a.title, lang)}</h2>
              {list(a.body[lang])}
            </section>
          ))}

          <p className="text-sm text-slate-500 mb-8">{L(termsEnacted, lang)}</p>

          {/* お問い合わせ */}
          <section>
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(contactCopy.title, lang)}</h2>
            <div className="text-slate-700 leading-relaxed space-y-3">
              <p>{L(contactCopy.body, lang)}</p>
              <div className="flex justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-4 py-2 rounded-lg border border-coral/20 hover:border-coral/30 text-coral hover:bg-coral/5 transition"
                >
                  {L(contactCopy.cta, lang)}
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;
